import { ToastUtilities } from "@libs/react-toastify/ToastUtilities";
import TypographyText from "@src/components/ui-components/TypographyText/TypographyText";
import { useAPISettings } from "@src/data-hooks/general-settings/settings.service";
import { getPluralRoleTitle, getPluralTenantUnitTitle } from "@src/utils/data/roles.utils";
import { Utilities } from "@src/utils/utilities";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import { UnitData } from "to-be-removed/LocalData/Property/Units/UnitData";
import { CompanyData } from "../../../to-be-removed/LocalData/Company/CompanyData";
import { ContractorGroupData } from "../../../to-be-removed/LocalData/ContractorGroup/ContractorGroupData";
import { ReadAccess } from "../../../to-be-removed/LocalData/General/AccessData";
import { InputElementContext } from "../../../to-be-removed/LocalData/General/DataObjectField/DataObjectField";
import { DataObjectHelpers } from "../../../to-be-removed/LocalData/General/DataObjects/DataObjectHelpers";
import { PropertyData } from "../../../to-be-removed/LocalData/Property/PropertyData";
import { Role } from "../../../to-be-removed/LocalData/User/Role";
import { UserData } from "../../../to-be-removed/LocalData/User/UserData";
import { ModalPopup } from "../ModalPopup/ModalPopup";
import { SelectInputData, SelectInputField } from "../SelectInputField/SelectInputField";
import { Text } from "../Text/Text";

export type ReadAccessGroup = "properties" | "companies" | "contractorGroups" | "roles" | "users" | "propertyUnits";

interface ReadAccessPopupProps {
  id: string;
  readAccess: ReadAccess | undefined;
  onSelect: (readAccess: ReadAccess) => void;
  accessGroups: ReadAccessGroup[];

  context: { propertyID?: string };
  requiredAccess?: ReadAccess;
  title?: string;
  label?: string;
  disableCurrentUserAccessCheck?: boolean;
}

export const ReadAccessPopup = (props: ReadAccessPopupProps) => {
  const { settings } = useAPISettings();
  const [, setLastUpdate] = useState<Date>(new Date());
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(props.readAccess?.roles ?? []);
  const [properties, setProperties] = useState<PropertyData[]>(props.readAccess?.properties ?? []);
  const [users, setUsers] = useState<UserData[]>(props.readAccess?.users ?? []);
  const [companies, setCompanies] = useState<CompanyData[]>(props.readAccess?.companies ?? []);
  const [contractorGroups, setContractorGroups] = useState<ContractorGroupData[]>(props.readAccess?.contractorGroups ?? []);
  const [propertyUnits, setPropertyUnits] = useState<UnitData[]>(props.readAccess?.propertyUnits ?? []);

  const isValidAccess = props.disableCurrentUserAccessCheck || Utilities.hasAccess(getReadAccess(), props.context?.propertyID);

  useEffect(() => {
    if (props.accessGroups.includes("contractorGroups")) {
      DataObjectHelpers.readAccess.contractorGroups.loadAllDatas({ propertyID: props.context?.propertyID }).then(() => setLastUpdate(new Date()));
    }
    if (props.accessGroups.includes("propertyUnits")) {
      DataObjectHelpers.readAccess.propertyUnits.loadAllDatas({ propertyID: props.context?.propertyID }).then(() => setLastUpdate(new Date()));
    }
  }, [props.accessGroups]);

  function onSelectRoles(options: SingleValue<SelectInputData> | MultiValue<SelectInputData>) {
    if (Array.isArray(options)) {
      const newRoles = options.map((option) => Role.getRoleWithId(parseInt(option.value)));
      setSelectedRoles(addRequiredElements(props.requiredAccess?.roles, newRoles));
    } else {
      setSelectedRoles(addRequiredElements(props.requiredAccess?.roles, [Role.LANDLORD]));
    }
  }

  function addRequiredElements<T>(required: T[] | undefined, elements: T[]): T[] {
    return [...new Set([...(required ?? []), ...elements])];
  }

  function getReadAccess(): ReadAccess {
    return {
      roles: selectedRoles.length > 0 ? selectedRoles : undefined,
      properties: properties.length > 0 ? properties : undefined,
      companies: companies.length > 0 ? companies : undefined,
      users: users.length > 0 ? users : undefined,
      contractorGroups: contractorGroups.length > 0 ? contractorGroups : undefined,
      propertyUnits: propertyUnits.length > 0 ? propertyUnits : undefined,
    };
  }

  function updateCompanies(data: CompanyData[] | undefined) {
    setCompanies(addRequiredElements(props.requiredAccess?.companies, data ?? []));
  }

  function updateUsers(data: UserData[] | undefined) {
    setUsers(addRequiredElements(props.requiredAccess?.users, data ?? []));
  }

  function handleActionClicked() {
    if (!isValidAccess) {
      ToastUtilities.presentError(t("error.removeAllReadAccess"));
      return;
    }
    props.onSelect(getReadAccess());
  }

  const context: InputElementContext = { menuPortalTarget: "popup", propertyID: props.context?.propertyID };

  return (
    <ModalPopup
      id={props.id}
      onActionClicked={handleActionClicked}
      disableAction={!isValidAccess}
      title={props.title ?? t("main.selectVisibility")}
      closable
      children={
        <div>
          {props.accessGroups.includes("roles") && (
            <>
              {props.label && <Text text={props.label} textAlign="left" />}
              <Text size="md" isBold text={t("main.roles")} marginTop={24} />
              <SelectInputField
                width="100%"
                placeholder={t("main.roles")}
                multi
                options={[Role.LANDLORD, Role.RENTER, Role.ENTREPRENEUR].map(
                  (role) => new SelectInputData(role.id + "", getPluralRoleTitle(role.id, settings?.data))
                )}
                value={selectedRoles.map((role) => role.id + "")}
                onChange={(categories) => onSelectRoles(categories)}
                menuPortalTarget="popup"
              />
            </>
          )}

          {props.accessGroups.includes("properties") && (
            <>
              <Text size="md" isBold text={t("main.properties")} marginTop={24} />
              {DataObjectHelpers.readAccess.properties.multiInputElement(
                properties,
                (data) => setProperties(addRequiredElements(props.requiredAccess?.properties, data ?? [])),
                context
              )}
            </>
          )}
          {props.accessGroups.includes("companies") && (
            <>
              <Text size="md" isBold text={t("main.companies")} marginTop={24} />
              {DataObjectHelpers.readAccess.companies.multiInputElement(companies, updateCompanies, context)}
            </>
          )}
          {props.accessGroups.includes("users") && (
            <>
              <Text size="md" isBold text={t("main.users")} marginTop={24} />
              {DataObjectHelpers.readAccess.users.multiInputElement(users, updateUsers, context)}
            </>
          )}
          {props.accessGroups.includes("contractorGroups") && !selectedRoles.includes(Role.ENTREPRENEUR) && (
            <>
              <Text size="md" isBold text={t("main.contractorGroups")} marginTop={24} />
              {DataObjectHelpers.readAccess.contractorGroups.multiInputElement(
                contractorGroups,
                (data) => setContractorGroups(addRequiredElements(props.requiredAccess?.contractorGroups, data ?? [])),
                context
              )}
            </>
          )}
          {props.accessGroups.includes("propertyUnits") && (
            <>
              <Text size="md" isBold text={getPluralTenantUnitTitle(settings?.data)} marginTop={24} />
              {DataObjectHelpers.readAccess.propertyUnits.multiInputElement(
                propertyUnits,
                (data) => setPropertyUnits(addRequiredElements(props.requiredAccess?.propertyUnits, data ?? [])),
                context
              )}
            </>
          )}

          {!isValidAccess && (
            <TypographyText fontStyle="italic" color="var(--red-500)" marginTop={16}>
              {t("error.removeAllReadAccess")}
            </TypographyText>
          )}
        </div>
      }
    />
  );
};
